import { PublicUserInfoType } from "../user/user";
import { PickupLocation } from "../transporter/transporter";
import { AppliedEstimatedRepairAddon } from "../expenses/expenseSettings";

type EquipmentStatus = 'yes' | 'no' | 'unknown';

/** How the buyer pays for a sold vehicle (entered via SoldDialog). */
type SalePaymentType = 'cash' | 'finance' | 'customer_bank';

type VehicleColor = 'White' | 'Blue' | 'Yellow' | 'Being painted';

/** Inventory quality tier. Blank until staff set it. Best → worst. */
export const VEHICLE_TIERS = ['One Plus', 'One', 'Two', 'Three'] as const;
export type VehicleTier = (typeof VEHICLE_TIERS)[number];

const VEHICLE_TIER_ALIASES: Record<string, VehicleTier> = {
  'one plus': 'One Plus',
  '1 plus': 'One Plus',
  '1+': 'One Plus',
  'tier 1+': 'One Plus',
  'tier 1 plus': 'One Plus',
  one: 'One',
  '1': 'One',
  'tier 1': 'One',
  two: 'Two',
  '2': 'Two',
  'tier 2': 'Two',
  three: 'Three',
  '3': 'Three',
  'tier 3': 'Three',
};

/** Map a stored value or a spoken label onto a tier. Unknown text is null. */
export function canonicalVehicleTier(value: unknown): VehicleTier | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if ((VEHICLE_TIERS as readonly string[]).includes(trimmed)) return trimmed as VehicleTier;
  return VEHICLE_TIER_ALIASES[trimmed.toLowerCase()] ?? null;
}

/**
 * Blank can become any tier. A set tier can only move up in quality
 * (toward One Plus). Returns an error message when the change is not allowed.
 */
export function vehicleTierRaiseError(current: unknown, next: unknown): string | null {
  const to = canonicalVehicleTier(next);
  if (!to) return `Tier must be ${VEHICLE_TIERS.join(', ')}.`;
  const from = canonicalVehicleTier(current);
  if (!from) return null;
  const fromRank = VEHICLE_TIERS.indexOf(from);
  const toRank = VEHICLE_TIERS.indexOf(to);
  if (toRank > fromRank) {
    return `Tier can move up toward One Plus, not down from ${from} to ${to}.`;
  }
  if (toRank === fromRank) return `This truck is already ${to}.`;
  return null;
}

/** One entry in the vehicle color change history (for timeline). */
export interface ColorHistoryEntry {
  color: VehicleColor;
  changed_at: string; // ISO date string
  changed_by_user_id?: string;
}

type TitleStatus =
  | 'purchased_for_inventory'   // Vehicle purchased for inventory (first step)
  | 'bank_information'          // Bank information collected and confirmed
  | 'payment_sent'              // Paid, needs title process to start
  | 'title_requested'           // Request sent to bank
  | 'in_transit'                // Has tracking number, waiting for delivery
  | 'title_shipped'             // Title has been shipped
  | 'physical_title_received'   // Title arrived (titles in house flow)
  | 'title_flip_required'       // Titles in house: answered Yes, flip needed
  | 'title_flip_not_required'  // Titles in house: answered No, no flip needed
  | 'title_flip_in_progress'    // Titles in house: flip required, marked in progress
  | 'title_flip_completed'      // Titles in house: flip photos uploaded
  | 'shipping_information'      // Titles in house: completed shipping info form
  | 'completed';                // Title workflow fully completed

interface DamageReportItem {
  part: string;
  severity: 'minor' | 'moderate' | 'severe';
  action: 'repair' | 'replace';
  cost_low: number;
  cost_high: number;
  explanation: string;
}

interface DamageReport {
  no_damage_detected: boolean;
  damage_report: DamageReportItem[];
  total_cost_low: number;
  total_cost_high: number;
  overall_explanation: string;
  generated_at?: string;
}

interface PhotoItem {
  id: string;
  url: string;
  vehicleId: string;
  vehicleInfo: string;
  uploadedAt?: string;
  angle: string;
  source?: string;
  damageReport?: DamageReport;
  hasAiReport?: boolean;
}

type TitleFormat = "electronic" | "paper";

interface Vehicle {
  // Core identification fields
  id: string;
  date_added: string | null;
  entered_by: string;
  vin: string;
  stock_number: string;
  updated_by: string | null;
  updated_at: string | null;
  date_available: string | null;

  // Basic vehicle information
  year: number | null;
  make: string | null;
  model: string | null;
  size: string | null;
  type: string | null;
  miles: number | null;
  fuel: "Gas" | "Diesel" | null;
  condition: "New" | "Used" | null;
  /** One Plus, One, Two, or Three. Null until set. */
  tier?: VehicleTier | null;
  color?: VehicleColor; // Default: "White"
  /** Date when vehicle color was set to "Being painted" (ISO string). */
  being_painted_date?: string | null;
  /** History of color changes for timeline display. */
  color_history?: ColorHistoryEntry[];
  gwvr_over_10k: EquipmentStatus; // GVWR over 10K lbs like other equipment

  // Financial information
  optic_list_price: number | null;
  seller_asking_price: number | null;
  profit: number | null;
  profit_realized?: boolean;
  payoff_amount: number | null;

  // Vehicle costs
  repair_cost?: number;
  shipping_cost?: number;
  rental_cost?: number;
  storage_cost?: number;
  other_cost?: number;

  // Sales information
  sold_date: string | null;
  who: string | null;
  bought_from: string | null;
  sold_to_user: {
    id: string;
    name: string;
  } | null;
  /**
   * CRM `contacts` doc id for the buyer. Preferred key for CRM sold-truck
   * queries; `sold_to_user` remains the portal Auth/user link.
   */
  sold_to_contact_id?: string | null;
  sold_by_user: {
    id: string;
    name: string;
  } | null;
  selling_to_user: {
    id: string;
    name: string;
  } | null;
  /** CRM contact id while Pending Sale / Active Deal (mirrors sold_to_contact_id). */
  selling_to_contact_id?: string | null;
  sold_location?: string | null;
  status:
    | "Pending Available"
    | "Available"
    | "Pending Sale"
    | "Sold"
    | "Sold elsewhere"
    | "Active Deal"
    | null;
  /** Date when vehicle status was set to "Pending Sale" (set when status moves to Pending Sale). */
  pending_sale_date?: string | null;
  /** Date when vehicle status was set to "Active Deal". */
  active_deal_date?: string | null;
  /** Date when vehicle status was set to "Pending Available". */
  pending_available_date?: string | null;
  /** Date when vehicle status was set to "Available". */
  available_date?: string | null;
  seller_type?: "Private Party" | "Auction" | "Dealer" | "Repo" | null;
  /** `auctions` doc id when seller_type is Auction — which auction the truck was purchased from. */
  auction_location_id?: string | null;
  title_format?: TitleFormat | null;
  /** US state abbreviation for where the title copy is from (required when uploading a title copy). */
  title_state?: string | null;
  /** When true, omit this truck from the weekday morning pending-sale title copy digest. */
  exclude_from_title_emails?: boolean;
  has_lien?: boolean | null;
  lien_lender?: string | null;
  lien_payoff_amount?: number | null;
  lien_good_through_date?: string | null;
  lien_account_number?: string | null;
  dealQuality: "great" | "good" | "average" | "belowAverage" | "poor" | null;

  // Contact and location details
  location: string | null;
  phone_number: string | null;
  email: string | null;
  assigned_user?: {
    id: string;
    email: string;
  };

  // Notes and additional information
  truck_notes?: string | null;
  follow_up_notes?: string | null;
  /** Optional notes for vehicle documents (e.g. in Manage Documents dialog). */
  document_notes?: string | null;
  /** Estimated body repair cost; part of estimated_repair_cost total. */
  estimated_body?: number | null;
  /** Estimated mechanical repair cost; part of estimated_repair_cost total. */
  estimated_mechanical?: number | null;
  /** Estimated safety repair cost; part of estimated_repair_cost total. */
  estimated_safety?: number | null;
  /** Sum of estimated_body + estimated_mechanical + estimated_safety (displayed as price). */
  estimated_repair_cost?: number | null;
  /**
   * Compact snapshot of expense-settings add-ons applied to this vehicle's
   * estimated repair categories (ids + amount + category only).
   */
  estimated_repair_addons?: AppliedEstimatedRepairAddon[];
  /** Explanation when linked expenses exceed the repair budget threshold. */
  repair_cost_over_budget_reason?: string | null;
  link: string | null;

  // Photo and display properties
  show_on_website: boolean;
  photo_storage_path: string | null;
  photos?: {
    main: string;
    front: string;
    back: string;
    left: string;
    right: string;
    interior: string;
    engine: string;
    trunk: string;
    other: string;
  };
  uploadedPhotoIds?: string[];

  // Equipment details
  cameras: EquipmentStatus;
  shelves: EquipmentStatus;
  size_confirmed?: boolean;

  favorite?: boolean;
  purchased_for_inventory?: boolean;
  /** Date when vehicle was marked as purchased for inventory (set when purchased_for_inventory is set to true). Not part of title flow. */
  purchased_for_inventory_date?: string | null;
  /**
   * Client sets true to request the "Pending sale not purchased" admin notification.
   * Cleared after the Functions listener fires (or left true as idempotent).
   */
  pending_sale_not_purchased_notify?: boolean;
  paid_for?: boolean;
  paid_for_date?: string | null;
  funded?: boolean;
  funded_date?: string | null;
  /** True when the sale is a TransLease lease (title/registration handled by TransLease). */
  translease_lease?: boolean;
  /** User IDs omitted from payroll payout for this specific vehicle. */
  payroll_omitted_user_ids?: string[] | null;
  delivered_to_customer?: boolean;

  warranty_purchaged?: boolean;

  warranty?: {
    warranty_terms: string;
    warranty_cost: number;
    customer_cost: number;
    warranty_booked: boolean;
    warranty_paid_for: boolean;
    warranty_created_at: Date | null;
    warranty_booked_at: Date | null;
    warranty_profit: number | null;
  }

  // Finance information
  finance?: {
    payment_type: SalePaymentType | null;
    down_payment: number | null;
    bank_name: string | null;
    amount_financed: number | null;
    points: number | null;
    profit: number | null;
    total_sale_price: number | null;
    tax_rate: number | null;
    include_tax_in_sale: boolean;
    include_warranty_in_sale: boolean;
    include_shipping_in_sale: boolean;
  } | null;

  shipping?: {
    shipped: boolean | null;
    /** Set when the truck is not being shipped. */
    staged?: boolean | null;
    /** Free text when the truck is neither shipped nor staged. */
    not_shipped_reason?: string | null;
    our_shipping_cost: number | null;
    customer_shipping_cost: number | null;
    profit: number | null;
  } | null;

  refinance?: boolean;
  pickup_location?: PickupLocation | null;
  rear_wheel?: 'SRW' | 'DRW' | null;

  // Add damages field for AI/inspection results
  damages?: DamageReportItem[];

  documents?: {
    title_copies?: string[];
    purchase_orders?: Array<string | {
      url: string;
      documentId: string;
      name: string;
      clientName?: string;
      generatedAt: string;
      templateName?: string;
    }>;
    payment_copies?: string[];
    invoices?: Array<{
      url: string;
      documentId: string;
      name: string;
      clientName?: string;
      generatedAt: string;
      templateName?: string;
    }>;
    additional_docs?: string[];
    /** Completed title flip documents (uploaded when title flip is done) */
    title_flip_docs?: string[];
  };

  gate_passes?: GatePassFile[];

  ready_for_transport?: ReadyForTransport | null;

  // Title tracking fields
  title_tracking?: {
    paid_date?: string | null;
    title_status?: TitleStatus;
    bank_contacted_date?: string | null;
    bank_name?: string | null;
    tracking_number?: string | null; // Legacy field, kept for backward compatibility
    inbound_tracking_number?: string | null; // Tracking number for inbound title (title_requested -> in_transit)
    outbound_tracking_number?: string | null; // Tracking number for outbound title (physical_title_received -> title_shipped -> completed)
    title_requested_date?: string | null;
    in_transit_date?: string | null;
    title_shipped_date?: string | null;
    physical_title_received_date?: string | null;
    /** True if title flip is required, false if not required. Set when step is completed. */
    title_flip_required?: boolean | null;
    title_flip_required_date?: string | null;
    title_flip_in_progress_date?: string | null;
    title_flip_completed_date?: string | null;
    /** Shipping info: documents required to mail. At least one checked to complete step. */
    shipping_info_title?: boolean | null;
    shipping_info_reassignment?: boolean | null;
    shipping_info_lien_release?: boolean | null;
    shipping_info_additional_paperwork?: boolean | null;
    shipping_info_completed_date?: string | null;
    title_in_house_date?: string | null;
    completed_date?: string | null;
    notes?: string | null;
    issue_reported?: boolean;
    issue_description?: string | null;
    major_issue?: boolean;
    last_updated_by?: string | null;
    last_updated_at?: string | null;
    /** Note recorded when an owner reverts title workflow status */
    revert_note?: string | null;
    revert_note_at?: string | null;
    revert_note_by?: string | null;
    shipping_labels?: string[]; // Array of label document IDs
    label_storage_path?: string | null; // Firebase Storage path to label PDF
  };

  // Inspection status fields
  mechanical_status?: 'N/A' | 'Need' | 'Pending' | 'Complete' | null;
  body_status?: 'N/A' | 'Need' | 'Pending' | 'Complete' | null;
  ready_for_sale?: boolean;
}

// TODO: Improve inventory types below

interface InventoryList {
  id: string;
  /** ISO timestamp string (stored as string in Firestore). */
  dateCreated: string | null;
  createdBy: string | null;
  inventoryIds: string[];
  name: string;
  description?: string;
  isArchived?: boolean;
  associatedUserIds?: string[];
}

type PhotoPosition =
  | "main"
  | "front"
  | "back"
  | "left"
  | "right"
  | "interior"
  | "engine"
  | "trunk"
  | "other";

interface SubmissionVehicle extends Vehicle {
  lien: string;
  financingBank: string;
  user_condition: string;
  damage: string;
  desiredPrice: number;
}
interface PhotoSubmissionForeignKey {
  photoId: string;
  userId: string;
}

interface PhotoSubmissionObject {
  userId: string;
  photoId: string;
  vehicleId: string;
  dateUploaded: string;
  photos: FirebasePhotoObject[];
  user?: PublicUserInfoType;
}

interface FirebasePhotoObject {
  fullPath: string;
  blobUrl: string;
}

interface VehiclePhoto {
  id: string;
  name: string;
  url: string;
  fullPath: string;
  position?: string;
}

interface UserInventoryList {
  id: string;
  userId: string;
  inventoryListId: string;
  dateAdded: string;
}

interface ReadyForTransport {
  needs_address: boolean;
  ready_for_pickup: boolean;
  customer?: boolean;
  repair?: boolean;
  note?: string | null;
  ready_for_transport_date?: string | null;
  hidden_from_needs_transport?: boolean;
}

interface GatePassFile {
  fileName: string;
  storagePath: string;
  url: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string | Date;
  uploadedBy: string;
}

export type {
  Vehicle,
  InventoryList,
  PhotoPosition,
  VehiclePhoto,
  PhotoItem,
  DamageReport,
  DamageReportItem,
  SubmissionVehicle,
  PhotoSubmissionObject,
  FirebasePhotoObject,
  PhotoSubmissionForeignKey,
  UserInventoryList,
  ReadyForTransport,
  GatePassFile,
  TitleStatus,
  EquipmentStatus,
  VehicleColor,
  TitleFormat,
  SalePaymentType,
};